/**
 * External dependencies
 */
import React from "react";
import { Translate } from "react-localize-redux";
import classNames from "classnames";

/**
 * Internal dependencies
 */

import { QueueBarber } from "../../types";
import BarberWaitTime from "./BarberWaitTime";
import { Barber } from "../../types";

/**
 * Assets
 */
import { ReactComponent as IconClose } from "../../assets/images/icon-login-dialog-close.svg";
import placeholderAvatar from "../../assets/images/avatar-placeholder.png";

const getShortName = (barber?: Barber): string => {
  if (!barber) return "";
  const first = barber.firstname || (barber.full_name ? barber.full_name.split(" ")[0] : "");
  const last = barber.lastname || (barber.full_name ? barber.full_name.split(" ").slice(-1)[0] : "");
  const lastInitial = last ? `${last.charAt(0).toUpperCase()}.` : "";
  return [first, lastInitial].filter(Boolean).join(" ").trim();
};

interface DropdownOption extends QueueBarber {
  onClick?: () => void;
  selectedOption?: number | Barber | null;
}

interface BarberDropDownProps {
  options: DropdownOption[];
  selectedOption: Barber | number | null;
  setIsOpen: (value: boolean) => void;
  isOpen: boolean;
  placeholder: string;
  queue?: number | false;
}

interface BarberDropDownMenuProps {
  title: string;
  placeholder: string;
  options: DropdownOption[];
  selectedOption: Barber | number | null;
  changeHandler: (value: Barber | null) => void;
  setIsOpen: (value: boolean) => void;
  isOpen: boolean;
}

/**
 * Constant for a default queue ID
 * This is used to represent a "no selection" state in the dropdown.
 * It can be adjusted based on your application's logic.
 * @TODO make better solution for this
 */

export const BarberDropDown: React.FC<BarberDropDownProps> = ({
  options,
  selectedOption,
  setIsOpen,
  isOpen,
  placeholder,
  queue,
}) => {
  const selected = options?.find((opt) => opt.barber.id === selectedOption);

  let resolvedImage: string | false = false;

  if (selected?.barber.avatar_active && selected?.barber.has_avatar && selected.barber.avatar) {
    resolvedImage = selected.barber.avatar;
  } else if (!selected?.barber.avatar_active && selected?.barber.has_image && selected.barber.image) {
    resolvedImage = selected.barber.image;
  }

  // Build an avatar stack from next available barbers (max 3)
  const getAvatarUrl = (barber?: any): string | undefined => {
    if (!barber) return undefined;
    const { avatar_active, has_avatar, avatar, has_image, image } = barber;

    if (avatar_active && has_avatar && avatar) return avatar as string;
    if (!avatar_active && has_image && image) return image as string;
    return typeof image === "string" ? (image as string) : undefined;
  };

  const getAvailableAvatarUrls = (
    list: DropdownOption[] = [],
    max: number = 3
  ): string[] => {
    if (!Array.isArray(list) || list.length === 0) return [];
    const urls = list
      .filter((opt) => Boolean(opt?.queue_open))
      .map((opt) => getAvatarUrl(opt?.barber))
      .filter((url): url is string => Boolean(url));
    return urls.slice(0, max);
  };

  const availableAvatarUrls = getAvailableAvatarUrls(options, 3);

  return (
    <div className="c-barber-dropdown">
      <div
        className="c-barber-dropdown__placeholder"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected ? (
          <>
            <img
              src={resolvedImage || placeholderAvatar}
              alt=""
              className="c-barber-dropdown__image"
            />
            <span className="c-barber-dropdown__label">
              {getShortName(selected.barber)}
            </span>
            {selected.queue_open && (
              <BarberWaitTime queue={selected.wait_time} />
            )}
          </>
        ) : (
          <>
            {availableAvatarUrls.length > 0 ? (
              <div className="c-barber-dropdown__image-stack">
                {availableAvatarUrls
                  .slice()
                  .reverse()
                  .map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={placeholder}
                      className="c-barber-dropdown__stack-image"
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        target.onerror = null;
                        target.src = placeholderAvatar;
                      }}
                    />
                  ))}
              </div>
            ) : (
              <img
                src={placeholderAvatar}
                alt={placeholder}
                className="c-barber-dropdown__image"
              />
            )}
            <span className="c-barber-dropdown__label">{placeholder}</span>
            {typeof queue === "number" && (
              <BarberWaitTime queue={queue} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const BarberDropDownMenu: React.FC<BarberDropDownMenuProps> = ({
  title,
  placeholder,
  options,
  selectedOption,
  changeHandler,
  setIsOpen,
}) => {
  const handleSelect = (value: Barber | null) => {
    changeHandler(value);
    setIsOpen(false);
  };

  return (
    <div className="c-barber-dropdown__menu">
      <button
        onClick={() => setIsOpen(false)}
        className="c-login-dialog__close"
      >
        <IconClose />
      </button>
      <h2 className="c-login-dialog__title">{title}</h2>

      <div
        className={`c-barber-dropdown__option${
          !selectedOption ? " is-selected" : ""
        }`}
        key="placeholder-option" // A unique key for this element
        onClick={() => handleSelect(null)}
      >
        <PlaceholderOption title={placeholder} />
      </div>

      {options?.map((option) => (
        <BarberDropDownOption
          key={option.barber.id}
          selectedOption={selectedOption}
          {...option}
          onClick={() => handleSelect(option.barber)}
        />
      ))}
    </div>
  );
};

const PlaceholderOption: React.FC<{ title: string }> = ({ title }) => {
  return (
    <>
      <img
        src={placeholderAvatar}
        alt={title}
        className="c-barber-dropdown__image"
      />
      <span className="c-barber-dropdown__label">{title}</span>
    </>
  );
};

// Make option component reusable
export const BarberDropDownOption: React.FC<DropdownOption> = ({
  barber,
  selectedOption,
  queue_open,
  wait_time,
  onClick,
}) => {
  const className = classNames("c-barber-dropdown__option", {
    "is-selected": selectedOption === barber.id,
  });

  let resolvedImage: string | false = false;

  if (barber?.avatar_active && barber?.has_avatar && barber?.avatar) {
    resolvedImage = barber.avatar;
  } else if (!barber?.avatar_active && barber?.has_image && barber?.image) {
    resolvedImage = barber.image;
  }
  return (
    <div className={className} id={barber.full_name} onClick={onClick}>
      <img
        src={resolvedImage || placeholderAvatar}
        alt=""
        className="c-barber-dropdown__image"
      />
      <span className="c-barber-dropdown__label">{getShortName(barber)}</span>
      {queue_open && <BarberWaitTime queue={wait_time} />}

      {!queue_open && (
        <span className="c-barber-dropdown__closed">
          <Translate id="barber.notIn" />
        </span>
      )}
    </div>
  );
};

export default BarberDropDown;
